import { useMemo } from 'react';
import { getAiPromptType } from '@client/util';
import type { Quote } from '@client/model';
import type { TraditionalLTCQuoteWithROI } from '@client/hooks/useTraditionalLTCQuotesWithROI';

interface QuoteWithROI extends Quote {
  roi?: number;
  roiValues?: any;
  formattedROI?: string;
}

interface UseUnderwritingResultsProps {
  adjustedQuotes: QuoteWithROI[];
  filteredTraditionalLTCQuotes: TraditionalLTCQuoteWithROI[];
  searchResults: Map<
    string,
    { status: 'eligible' | 'ineligible' | 'unsure' | null; fullData?: any }
  >;
  selectedTab: ShopInsuranceTab;
  underwritingFetchComplete: boolean;
  selectedEligibilityFilters: string[];
}

export function useUnderwritingResults({
  adjustedQuotes,
  filteredTraditionalLTCQuotes,
  searchResults,
  selectedTab,
  underwritingFetchComplete,
  selectedEligibilityFilters,
}: UseUnderwritingResultsProps) {
  // Map underwriting results to quote IDs
  const underwritingResults = useMemo(() => {
    const results = new Map<string, 'eligible' | 'ineligible' | 'unsure' | null>();

    if (searchResults.size === 0) {
      return results;
    }

    adjustedQuotes.forEach(quote => {
      const aiPromptType = getAiPromptType('', quote.productName, selectedTab === 'joint');
      if (aiPromptType) {
        const resultKey = `${quote.productName}:${aiPromptType}`;
        const underwritingResult = searchResults.get(resultKey);
        if (underwritingResult !== undefined) {
          results.set(quote.insuranceQuoteId, underwritingResult.status);
        }
      }
    });

    filteredTraditionalLTCQuotes.forEach(quote => {
      const aiPromptType = getAiPromptType('', quote.productName, selectedTab === 'joint');
      if (aiPromptType) {
        const resultKey = `${quote.productName}:${aiPromptType}`;
        const underwritingResult = searchResults.get(resultKey);
        if (underwritingResult !== undefined) {
          results.set(quote.traditionalLTCQuoteId, underwritingResult.status);
        }
      }
    });

    return results;
  }, [searchResults, adjustedQuotes, filteredTraditionalLTCQuotes, selectedTab]);

  // Calculate eligibility counts
  const eligibilityCounts = useMemo(
    () => ({
      eligible:
        adjustedQuotes.filter(q => underwritingResults.get(q.insuranceQuoteId) === 'eligible')
          .length +
        filteredTraditionalLTCQuotes.filter(
          q => underwritingResults.get(q.traditionalLTCQuoteId) === 'eligible',
        ).length,
      unsure:
        adjustedQuotes.filter(q => underwritingResults.get(q.insuranceQuoteId) === 'unsure')
          .length +
        filteredTraditionalLTCQuotes.filter(
          q => underwritingResults.get(q.traditionalLTCQuoteId) === 'unsure',
        ).length,
      ineligible:
        adjustedQuotes.filter(q => underwritingResults.get(q.insuranceQuoteId) === 'ineligible')
          .length +
        filteredTraditionalLTCQuotes.filter(
          q => underwritingResults.get(q.traditionalLTCQuoteId) === 'ineligible',
        ).length,
    }),
    [adjustedQuotes, filteredTraditionalLTCQuotes, underwritingResults],
  );

  // Filter quotes by eligibility
  const filteredAdjustedQuotes = useMemo(() => {
    if (!underwritingFetchComplete || selectedEligibilityFilters.length === 0) {
      return adjustedQuotes;
    }

    return adjustedQuotes.filter(quote => {
      const status = underwritingResults.get(quote.insuranceQuoteId);
      if (!status) return true;
      return selectedEligibilityFilters.includes(status);
    });
  }, [adjustedQuotes, underwritingResults, underwritingFetchComplete, selectedEligibilityFilters]);

  const filteredTraditionalLTCQuotesWithEligibility = useMemo(() => {
    if (!underwritingFetchComplete || selectedEligibilityFilters.length === 0) {
      return filteredTraditionalLTCQuotes;
    }

    return filteredTraditionalLTCQuotes.filter(quote => {
      const status = underwritingResults.get(quote.traditionalLTCQuoteId);
      if (!status) return true;
      return selectedEligibilityFilters.includes(status);
    });
  }, [
    filteredTraditionalLTCQuotes,
    underwritingResults,
    underwritingFetchComplete,
    selectedEligibilityFilters,
  ]);

  const hasValidUnderwritingResults = Array.from(underwritingResults.values()).some(
    result => result !== null,
  );

  const showEligibilityFilter = underwritingFetchComplete && hasValidUnderwritingResults;

  return {
    underwritingResults,
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
  };
}
